import { App, AppInfo } from '@capacitor/app';
import { AsyncSubject, ReplaySubject, firstValueFrom } from 'rxjs';
import { LoadingController, Platform } from '@ionic/angular';
import {
  LiveUpdateService,
  LiveUpdateTarget,
  SnapshotInfo,
  SyncOutcome,
} from './plugin/live-update.service';

import { Capacitor } from '@capacitor/core';
import { Injectable, inject } from '@angular/core';
import { LoggingService } from './logging.service';
import { NotificationService } from './notification.service';
import { buildInfo } from '../../config/build-info';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  private liveUpdates = inject(LiveUpdateService);
  private platform = inject(Platform);
  private loggingService = inject(LoggingService);
  notificationService = inject(NotificationService);
  loadingCtrl = inject(LoadingController);

  // this may go elsewhere but need to display on ui for now
  snapshotInfo: SnapshotInfo = null;
  snapshotInfo$ = new ReplaySubject<SnapshotInfo>(1);

  // putting this here since it's a singleton and will persist for life of app instance
  showPlayStoreUpgradeButton = false;
  upgradeMessage = '';
  forceUpdateBeforeLogin = false;

  lastCheckForUpdate: number = 0;

  config: LiveUpdateTarget = {
    appId: buildInfo.ionicAppId,
    channel: buildInfo.ionicAppChannel,
  };
  private readonly gtm = (<any>window).dataLayer || [];

  readonly initialized$ = new AsyncSubject<boolean>();

  constructor() {
    this.setSnapShotInfo()
      .then(() => {
        this.initialized$.next(true);
      })
      .catch(err => {
        console.error('error initializing platform', err);
        this.initialized$.next(false);
      })
      .finally(() => this.initialized$.complete());
  }

  /**
   * The applied snapshot at startup comes from this app's own record, written on every sync —
   * the new SDK has no getCurrentVersion(). Same visible behaviour as before: empty on a fresh
   * install until the first sync, populated from then on.
   */
  private async setSnapShotInfo() {
    if (!this.isMobilePlatform) {
      return;
    }
    await this.platform.ready();
    this.snapshotInfo = (await this.liveUpdates.getAppliedSnapshot()) || null;
    this.snapshotInfo$.next(this.snapshotInfo);
    this.updateGtmDataLayer(this.snapshotInfo);
    console.info(`Current snapshot version found. `, this.snapshotInfo);
  }

  resetUpgradeVariables() {
    this.showPlayStoreUpgradeButton = false;
    this.upgradeMessage = '';
    this.forceUpdateBeforeLogin = false;
  }

  // this always gets called after we register our device on the server and know what channel it should point to
  setChannel(channel: string) {
    // if we're changing the channel, we should get fresh variables so we can test the new version's validity
    this.resetUpgradeVariables();
    this.config.channel = channel;
  }

  async getAppInfo(): Promise<AppInfo> {
    await firstValueFrom(this.initialized$);
    return this.isMobilePlatform
      ? await App.getInfo()
      : { name: '', id: '', build: '0', version: '0' };
  }

  async checkForNewVersion(silent: boolean = false, force: boolean = false) {
    const initialized = await firstValueFrom(this.initialized$);
    if (!initialized) {
      throw new Error('failed to initialize version service');
    }
    const timeSinceLastChecked = new Date().getTime() - this.lastCheckForUpdate;

    //	if it's been more than 10 minutes let's check again
    if (!force && timeSinceLastChecked < 60 * 10 * 1000) {
      return;
    }

    this.lastCheckForUpdate = new Date().getTime();
    await this.platform.ready();

    // return if not on device
    if (!this.isMobilePlatform) {
      this.gtm.push({ app_channel: 'sim' });
      if (!silent) this.notificationService.info('Software is up to date');
      return;
    }

    if (!this.config.channel || this.config.channel === 'configurable') {
      console.warn('You cannot update unless the channel has been defined.');
      if (!silent)
        this.notificationService.info(`You cannot update unless the channel has been defined.`);
      return;
    }

    const loading = await this.loadingCtrl.create({
      spinner: 'crescent',
      message: 'Checking for updates...',
    });
    loading.present();

    try {
      //	this is disabled locally in capacitor.config.ts (LiveUpdates.enabled) and re-enabled on
      //	the build server, to prevent the app from checking for updates during local development.
      const info = await this.liveUpdates.getConfiguration();
      console.debug('deploy info', info);
      if (info.disabled) {
        console.warn('deploy disabled');
        this.notificationService.info('Live updates are disabled in development mode.');
        loading.dismiss();
        return;
      }

      // 	this assumes channel is already correctly set on config
      //	we need to override the channel here because the plugin will append the channel to the app id
      //	we do this last minute and let everything else work exactly the same where the backend tells it what channel to use
      const appOverrideChannel = this.config;
      if (
        environment.features.overrideChannelName &&
        appOverrideChannel.channel.indexOf(environment.appName) < 0
      ) {
        appOverrideChannel.channel = `${environment.appName}_${appOverrideChannel.channel}`;
      }
      await this.liveUpdates.configure(appOverrideChannel);

      //	One call both checks and downloads. When the device is already up to date it reports the
      //	active snapshot back, which keeps our record (and the backend's) fresh.
      const outcome = await this.liveUpdates.sync(progress => {
        loading.message = `Applying updates ${Math.round(progress * 100)}%. App will restart when finished.`;
      });

      this.loggingService.setVersion(outcome.snapshot?.buildId || '');
      this.refreshSnapshotInfo(outcome);

      if (this.shouldReload(outcome)) {
        // We found an update, and are in process of restarting onto it
        loading.message = `Applying updates. App will restart when finished`;
        await loading.dismiss();
        await this.liveUpdates.reload();
        return;
      }

      loading.message = `Software is up to date`;
      if (!silent) this.notificationService.info('Software is up to date');
    } catch (err) {
      console.error('error applying snapshot', err);
      if (!this.config?.channel?.toLocaleLowerCase()?.includes('production')) {
        this.notificationService.warning(
          "There was an error downloading updates. Click check for updates until you don't get this error anymore",
        );
      }
    } finally {
      loading.dismiss();
    }
  }

  private shouldReload(outcome: SyncOutcome): boolean {
    return shouldRestartOnto(outcome, buildInfo.baselineBuildId);
  }

  private refreshSnapshotInfo({ snapshot }: SyncOutcome) {
    const changed = (snapshot?.versionId || null) !== (this.snapshotInfo?.versionId || null);
    if (!changed) {
      return;
    }
    this.snapshotInfo = snapshot || null;
    this.snapshotInfo$.next(this.snapshotInfo);
    this.updateGtmDataLayer(this.snapshotInfo);
  }

  private get isMobilePlatform() {
    return Capacitor.isNativePlatform();
  }

  private updateGtmDataLayer(value: SnapshotInfo) {
    this.gtm.push({
      app_channel: this.isMobilePlatform ? value?.channel : 'sim',
      app_flow_build: value?.buildId,
      binary_version: value?.binaryVersionCode,
    });
  }
}

/**
 * Restart only when the staged files actually changed AND they are not the build this binary
 * already ships. The old flow asked the server for the next build id up front and skipped the
 * whole sync when it matched `baselineBuildId`; the new SDK has no pre-download check, so the
 * comparison moved after the sync — the download happens once per release (differential, so
 * small), and the pointless restart it used to prevent is still prevented.
 */
export function shouldRestartOnto(
  { snapshot, applied }: SyncOutcome,
  baselineBuildId: number,
): boolean {
  if (!applied || !snapshot) {
    return false;
  }

  const buildId = Number.isNaN(Number(snapshot.buildId)) ? -1 : Number(snapshot.buildId);
  if (buildId === baselineBuildId) {
    console.info(
      `The applied snapshot is the baseline build, "${baselineBuildId}" -- no need to restart.`,
    );
    return false;
  }
  return true;
}
